interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiresIn: number; // em milissegundos
}

class CacheService {
  private cache: Map<string, CacheItem<unknown>> = new Map();
  get<T>(key: string): T | null {
    const item = this.cache.get(key);

    if (!item) {
      return null;
    }

    const now = Date.now();
    const isExpired = now - item.timestamp > item.expiresIn;

    if (isExpired) {
      this.cache.delete(key);
      return null;
    }

    return item.data as T;
  }

  set<T>(key: string, data: T, expiresIn: number = 5 * 60 * 1000): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expiresIn,
    });
  }

  remove(key: string): void {
    this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
  }

  clearExpired(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    this.cache.forEach((item, key) => {
      const isExpired = now - item.timestamp > item.expiresIn;
      if (isExpired) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => {
      this.cache.delete(key);
    });
  }

  getStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }
}

export const cacheService = new CacheService();

setInterval(() => {
  cacheService.clearExpired();
}, 10 * 60 * 1000);

export function withCache<TArgs extends unknown[], TReturn>(
  fn: (...args: TArgs) => Promise<TReturn>,
  getCacheKey: (...args: TArgs) => string,
  expiresIn: number = 5 * 60 * 1000,
) {
  return async (...args: TArgs): Promise<TReturn> => {
    const cacheKey = getCacheKey(...args);

    const cached = cacheService.get<TReturn>(cacheKey);
    if (cached !== null) {
      return cached;
    }

    const result = await fn(...args);

    cacheService.set(cacheKey, result, expiresIn);

    return result;
  };
}

export default cacheService;
